import React, { useEffect, useState } from 'react';
import { Check, LogOut, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContextSupabase';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface Plan {
  id: string;
  name: string;
  stripe_price_id: string;
  seat_limit: number | null;
  price_monthly: number;
  features: string[];
}

export default function Pricing() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentUser, subscription } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  console.log('Pricing page - currentUser:', currentUser?.email);
  console.log('Pricing page - subscription:', subscription);

  useEffect(() => {
    const isUpgradeMode = searchParams.get('upgrade') === 'true';

    if (currentUser) {
      const isSwipeUppEmail = currentUser.email?.endsWith('@swipe-upp.com');

      if (isSwipeUppEmail || (subscription && !isUpgradeMode)) {
        navigate('/app/discover');
        return;
      }
    }

    loadPlans();
  }, [currentUser, subscription, navigate, searchParams]);

  async function loadPlans() {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('price_monthly', { ascending: true });

      if (error) throw error;
      setPlans(data || []);
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleLogout() {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  }

  async function handleSubscribe(priceId: string) {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            priceId,
            userId: currentUser.id,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        throw new Error(errorData.error || 'Erreur lors de la création de la session de paiement');
      }

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('URL de paiement non reçue');
      }
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      alert(error.message || 'Une erreur est survenue lors de la création de la session de paiement. Veuillez réessayer.');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600">Chargement...</div>
      </div>
    );
  }

  const isUpgradeMode = searchParams.get('upgrade') === 'true';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {currentUser && (
          <div className="flex justify-between items-center mb-4">
            {isUpgradeMode && (
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 shadow-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour
              </button>
            )}
            <div className={isUpgradeMode ? '' : 'ml-auto'}>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 shadow-sm"
              >
                <LogOut className="w-4 h-4" />
                Se déconnecter
              </button>
            </div>
          </div>
        )}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Choisissez votre plan
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Toutes les fonctionnalités incluses. Seul le nombre de sièges diffère.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => {
            const isCurrentPlan = subscription?.plan_id === plan.id;
            const isPopular = index === 1;

            return (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl shadow-xl overflow-hidden transition-transform hover:scale-105 ${
                  isPopular ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                {isPopular && (
                  <div className="absolute top-0 right-0 bg-blue-500 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                    Populaire
                  </div>
                )}

                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>

                  <div className="mb-6">
                    <span className="text-4xl font-bold text-gray-900">
                      {(plan.price_monthly / 100).toFixed(0)}€
                    </span>
                    <span className="text-gray-600 ml-2">/mois</span>
                  </div>

                  <div className="mb-6 pb-6 border-b border-gray-200">
                    <p className="text-gray-600 font-medium">
                      {plan.seat_limit === null
                        ? 'Sièges illimités'
                        : `${plan.seat_limit} siège${plan.seat_limit > 1 ? 's' : ''}`}
                    </p>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleSubscribe(plan.stripe_price_id)}
                    disabled={isCurrentPlan}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                      isCurrentPlan
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : isPopular
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                  >
                    {isCurrentPlan ? 'Plan actuel' : 'Choisir ce plan'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-4">
            Besoin d'un plan sur mesure pour votre entreprise ?
          </p>
          <button className="text-blue-600 font-semibold hover:text-blue-700">
            Contactez-nous
          </button>
        </div>
      </div>
    </div>
  );
}
